import { useEffect, useMemo, useState } from 'react';
import Sheet from '../Sheet';
import ButtonSpinner from '../ButtonSpinner';
import { BTN_PRIMARY } from '../buttonStyles';
import { busyStyle } from '../../utils/busyStyle';
import { markBookingsQuiet } from '../../utils/oneToast';
import { setLiveWeeklyTime, subscribeCoachWeek, type CoachWeek, type LessonMode } from '../../services/liveV2';
import { liveLessonsErrorMessage } from '../../services/liveLessons';
import { useToast } from '../../hooks/useToast';
import { weeklyChoices } from '../../utils/monthlyCoaching';
import { useLiveConfig } from '../../hooks/useLiveConfig';
import { SECTION_HEADER } from './surfaces';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** The shared header, 24 above its group (components.md 9). */
const SECTION = `mt-6 ${SECTION_HEADER}`;

function Chip({ on, children, onClick, label, fill = false }: {
  on: boolean; children: React.ReactNode; onClick: () => void; label?: string; fill?: boolean;
}) {
  return (
    <button type="button" aria-pressed={on} aria-label={label} onClick={onClick}
      className={
        `ios-press h-11 cursor-pointer rounded-[100px] ${fill ? 'w-full px-0' : 'px-4'} text-[15px] leading-[20px] font-medium tabular-nums ` +
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40 ' +
        (on ? 'bg-biscayne text-white' : 'border border-[rgba(0,0,0,0.08)] bg-white text-db-text-1')
      }>
      {children}
    </button>
  );
}

/**
 * Set a weekly time (redesign spec, batch 4): the coach's default week as
 * weekdays and starts, in the player's zone; pick one, and a mode, and it
 * holds from next week forward (setLiveWeeklyTime). Lessons already booked
 * at other times stay where they are.
 */
export function WeeklyTimeSheet({
  membershipId, coachId, coachFirstName, tz, initial, onClose,
}: {
  membershipId: string;
  coachId: string;
  coachFirstName: string;
  tz: string;
  initial?: { weekday: number; time: string; lessonMode: LessonMode } | null;
  onClose: (line?: string) => void;
}) {
  const { showError } = useToast();
  const [week, setWeek] = useState<CoachWeek | null | undefined>(undefined);
  useEffect(() => subscribeCoachWeek(coachId, setWeek), [coachId]);
  const [now] = useState(() => Date.now());
  // The slot size is config/live's, never a half hour assumed.
  const config = useLiveConfig();
  const choices = useMemo(() => (week && config
    ? weeklyChoices(week.week, week.timeZone, tz, now, config.slotMinutes) : new Map()), [week, tz, now, config]);
  const days = [...choices.keys()].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7));
  const [weekday, setWeekday] = useState<number | null>(initial?.weekday ?? null);
  const [time, setTime] = useState<string | null>(initial?.time ?? null);
  const [mode, setMode] = useState<LessonMode>(initial?.lessonMode ?? 'match_review');
  const [saving, setSaving] = useState(false);
  const day = weekday ?? days[0] ?? null;
  const times = day != null ? choices.get(day) ?? [] : [];

  const save = async () => {
    if (day == null || !time) return;
    setSaving(true);
    try {
      // The lessons it books are said by its one toast, not one each (utils/oneToast).
      markBookingsQuiet();
      const r = await setLiveWeeklyTime({ membershipId, weekday: day, time, lessonMode: mode });
      onClose(r.line);
    } catch (err) {
      showError(liveLessonsErrorMessage(err, 'Could not set your weekly time. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet title="Weekly Time" onClose={() => onClose()} busy={saving}>
      {week === undefined ? null : !days.length ? (
        <p className="text-[15px] leading-[20px] text-db-text-2">{coachFirstName} has no weekly hours set yet.</p>
      ) : (
        <>
          <p className="text-[15px] leading-[20px] text-db-text-2">{coachFirstName}&apos;s hours, in your time zone.</p>
          <h3 className={SECTION}>Day</h3>
          {/* One row of equal days, seven to the week, so Sat never wraps alone (audit 30). */}
          <div className="grid grid-cols-7 gap-2">
            {days.map((d) => (
              <Chip key={d} fill on={d === day} onClick={() => { setWeekday(d); setTime(null); }}>{DAYS[d]}</Chip>
            ))}
          </div>
          <h3 className={SECTION}>Time</h3>
          <div className="grid grid-cols-3 gap-2">
            {times.map((c: { time: string; label: string }) => (
              <Chip key={c.time} fill on={c.time === time} onClick={() => setTime(c.time)}>{c.label}</Chip>
            ))}
          </div>
          <h3 className={SECTION}>Lesson</h3>
          <div className="flex flex-wrap gap-2">
            <Chip on={mode === 'match_review'} onClick={() => setMode('match_review')}>Match Review</Chip>
            <Chip on={mode === 'guidance_call'} onClick={() => setMode('guidance_call')}>Guidance Call</Chip>
          </div>
          <p className="mt-3 text-[13px] leading-[18px] text-db-text-2">
            It holds from next week forward. Lessons already booked at other times stay where they are.
          </p>
          <button type="button" onClick={() => { void save(); }} disabled={day == null || !time || saving}
            aria-busy={saving} style={busyStyle(saving)} className={`${BTN_PRIMARY} mb-2 mt-6 w-full`}>
            {saving ? <ButtonSpinner /> : 'Set Weekly Time'}
          </button>
        </>
      )}
    </Sheet>
  );
}
